import type { VoidFunctionComponent } from 'react';

import { BINARY_DECOMPOSITION } from '../../../../shared/featureFlags/flagNames';
import { ProjectSource } from '../../../../typings/enums';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import type { AnyFilter } from '../../../../shared/organizationPackages/filters';
import type { ProjectSources } from '../../../../typings/enums';
import type { SelectOption } from '../../../types';

const SOURCE_OPTIONS: SelectOption[] = [
  { value: ProjectSource.Provided, label: 'Provided via CLI' },
  { value: ProjectSource.Autobuild, label: 'Quick import' },
  { value: ProjectSource.SBOM, label: 'SBOM import' },
  { value: ProjectSource.Container, label: 'Container' },
  { value: ProjectSource.Archive, label: 'Archive upload' },
  { value: ProjectSource.Binary, label: 'Binary Decomposition' },
];

type Props = {
  sources: ProjectSources[];
  setFilters: (filter: Partial<AnyFilter>) => void;
};

/**
 * PackageInventoryFiltersProjectSource displays all of the possible sources for the parent project that the user can filter by.
 */
const PackageInventoryFiltersProjectSource: VoidFunctionComponent<Props> = ({ sources, setFilters }) => {
  // map current sources to SelectOption format
  const selectedCheckboxes = sources.map((source: string) => ({
    label: SOURCE_OPTIONS.find(option => option.value === source)?.label || source,
    value: source,
  }));

  // handle onChange from FilterCardCheckboxes
  const onChange = (newSelected: SelectOption[]) => {
    const selectedSources = newSelected.map(({ value }) => value as ProjectSources);
    setFilters({ sources: selectedSources });
  };

  // Binary Decomposition input source should only be displayed when feature is enabled.
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const options = SOURCE_OPTIONS.filter(
    entry => (isBinaryDecompositionEnabled || entry.value !== ProjectSource.Binary));

  return (
    <FilterCardCheckboxes
      label="Import method"
      options={options}
      values={selectedCheckboxes}
      onChange={onChange}
      testId="package-inventory-filters--project-source"
    />
  );
};

export default PackageInventoryFiltersProjectSource;
